import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { STATUS_FILTERS, buildListParams, canCreateMission, formatDateTime, formatPeriod, missionErrorMessage, statusLabel, typeChoices } from "../lib/missions";
import { MissionStatusBadge, ReportStateBadge } from "../components/missions/MissionBits";
import { useMissionTypes } from "../components/missions/MissionTypesPanel";
import MissionPlanning from "../components/missions/MissionPlanning";
import AttendanceSummary from "../components/attendance/AttendanceSummary";
import { canManageProcedures } from "../lib/procedures";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, ClipboardCheck, Loader2, MapPinned, Plus, RefreshCw, Search, Settings2, SlidersHorizontal, Users } from "lucide-react";

const ALL = "__all__";
const PAGE_SIZE = 50;

function defaultTab(user) {
  if (user?.role === "admin" || hasPermission(user, ["missions:view_all", "missions:manage", "missions:manage_all"])) return "all";
  return "mine";
}

// Missions terrain. The list, its filters and its order come from the server, which only returns what the person may
// see: « Mes missions » = the missions whose team I am in; « Toutes » = everything my rights show (all, or the ones I created).
export default function Missions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showTimeTab = !!user?.features?.attendance;
  const showReviewTab = hasPermission(user, ["missions:manage", "missions:manage_all"]); // organisers decide on submitted reports
  const tab = ["mine", "all", "planning", ...(showTimeTab ? ["time"] : []), ...(showReviewTab ? ["review"] : [])].includes(searchParams.get("tab"))
    ? searchParams.get("tab") : defaultTab(user);
  const planningTab = tab === "planning" || tab === "time" || tab === "review"; // tabs that are not the mission list
  const setTab = (value) => setSearchParams((p) => { const next = new URLSearchParams(p); next.set("tab", value); return next; }, { replace: true });
  const showAllTab = defaultTab(user) === "all";
  const canCreate = canCreateMission(user);
  const canManageTypes = hasPermission(user, "missions:manage_types") && hasPermission(user, "collaborators:view");

  const [status, setStatus] = useState("");
  const [typeId, setTypeId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const { types } = useMissionTypes();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(() => buildListParams({ tab, status, typeId, search, limit: PAGE_SIZE }), [tab, status, typeId, search]);

  const loadFirst = useCallback(async () => {
    if (planningTab) return;
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data } = await api.get("/missions", { params });
      if (mine !== seq.current) return;
      setItems(data.items);
      setTotal(data.total);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(missionErrorMessage(e, "Impossible de charger les missions."));
      setPhase("error");
    }
  }, [params, planningTab]);
  useEffect(() => { loadFirst(); return () => { seq.current += 1; }; }, [loadFirst]);

  const loadMore = async () => {
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/missions", { params: { ...params, skip: items.length } });
      if (mine !== seq.current) return;
      setItems((prev) => [...prev, ...data.items.filter((d) => !prev.some((p) => p.id === d.id))]);
      setTotal(data.total);
    } catch (e) {
      if (mine === seq.current) toast.error(missionErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const filtered = !!status || !!typeId || !!search.trim();
  const reset = () => { setStatus(""); setTypeId(""); setSearchInput(""); setSearch(""); };
  const open = (m) => navigate(`/missions/${m.id}`);

  const statusSelect = (
    <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-full md:w-44" aria-label="Statut" data-testid="missions-status-filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Tous les statuts</SelectItem>
        {STATUS_FILTERS.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const typeSelect = (
    <Select value={typeId || ALL} onValueChange={(v) => setTypeId(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-full md:w-48" aria-label="Type" data-testid="missions-type-filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Tous les types</SelectItem>
        {typeChoices(types, typeId).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}{t.active ? "" : " (inactif)"}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const activeFilterCount = (status ? 1 : 0) + (typeId ? 1 : 0);

  return (
    <div className="space-y-4" data-testid="missions-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Missions</h1>
          <p className="text-sm text-slate-500">Les interventions de l'équipe dans les pharmacies : qui y va, quand, et où en est chaque mission.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canManageProcedures(user) && (
            <Button variant="outline" asChild className="h-9 hidden sm:inline-flex">
              <Link to="/missions/procedures" data-testid="missions-procedures-link"><ClipboardCheck size={15} className="mr-1.5" />Procédures</Link>
            </Button>
          )}
          {canManageTypes && (
            <Button variant="outline" asChild className="h-9 hidden sm:inline-flex">
              <Link to="/collaborateurs/organisation?tab=mission_types" data-testid="missions-types-link"><Settings2 size={15} className="mr-1.5" />Types de mission</Link>
            </Button>
          )}
          {canCreate && (
            <Button asChild className="h-9">
              <Link to="/missions/nouvelle" data-testid="missions-new"><Plus size={16} className="mr-1.5" />Nouvelle mission</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Missions affichées">
        {[["mine", "Mes missions"], ...(showAllTab ? [["all", "Toutes"]] : []), ["planning", "Planning"], ...(showTimeTab ? [["time", "Temps"]] : []),
          ...(showReviewTab ? [["review", "À valider"]] : [])].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`missions-tab-${id}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "time" ? <AttendanceSummary /> : tab === "review" ? <ReportsToReview /> : planningTab ? <MissionPlanning /> : (<>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0 md:max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Titre ou référence…" className="pl-9 h-9 text-base md:text-sm"
            aria-label="Rechercher une mission" data-testid="missions-search" />
        </div>
        <div className="hidden md:flex items-center gap-2">{statusSelect}{typeSelect}</div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="md:hidden h-9 gap-1.5 shrink-0" aria-label="Filtres" data-testid="missions-filters-toggle">
              <SlidersHorizontal size={14} />{activeFilterCount > 0 && <span className="text-xs font-semibold">{activeFilterCount}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3 md:hidden">{statusSelect}{typeSelect}</PopoverContent>
        </Popover>
      </div>

      {phase === "loading" && (
        <div className="space-y-2" data-testid="missions-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      )}

      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="missions-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={loadFirst}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="missions-empty">
          <MapPinned className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucune mission ne correspond</h3>
              <p className="text-sm text-slate-500">Modifiez ou effacez les filtres pour élargir la liste.</p>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : tab === "mine" ? (
            <>
              <h3 className="font-medium text-slate-900">Aucune mission pour vous</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Les missions où vous faites partie de l'équipe apparaîtront ici.</p>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucune mission pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Créez une mission pour planifier une installation, une formation ou une visite dans une pharmacie.</p>
              {canCreate && (
                <Button asChild size="sm"><Link to="/missions/nouvelle"><Plus size={14} className="mr-1.5" />Nouvelle mission</Link></Button>
              )}
            </>
          )}
        </Card>
      )}

      {phase === "ready" && items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="missions-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mission</TableHead><TableHead>Pharmacie</TableHead><TableHead className="hidden lg:table-cell">Type</TableHead>
                  <TableHead>Dates prévues</TableHead><TableHead>Statut</TableHead><TableHead className="hidden lg:table-cell">Responsable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => open(m)} data-testid={`mission-row-${m.reference}`}>
                    <TableCell className="max-w-[18rem]">
                      <Link to={`/missions/${m.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:underline line-clamp-1">{m.title}</Link>
                      <div className="text-xs text-slate-500">{m.reference}</div>
                    </TableCell>
                    <TableCell className="max-w-[14rem] truncate">{m.prospect.pharmacy_name}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{m.type.label}</TableCell>
                    <TableCell className="text-slate-600 whitespace-nowrap">{formatPeriod(m.planned_start_at, m.planned_end_at, m.timezone)}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1"><MissionStatusBadge status={m.status} /><ReportStateBadge status={m.report_status} /></div></TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">
                      {m.lead ? m.lead.name : <span className="text-slate-400">À désigner</span>}
                      {m.team.length > 1 && <span className="ml-1 text-xs text-slate-400">+{m.team.length - 1}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="md:hidden divide-y divide-slate-100 overflow-hidden" data-testid="missions-list-mobile">
            {items.map((m) => (
              <Link key={m.id} to={`/missions/${m.id}`} className="flex items-start gap-3 p-4 active:bg-slate-50" data-testid={`mission-card-${m.reference}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-slate-900 break-words">{m.title}</span>
                    <div className="flex flex-col items-end gap-1 shrink-0"><MissionStatusBadge status={m.status} /><ReportStateBadge status={m.report_status} /></div>
                  </div>
                  <div className="text-sm text-slate-600 truncate">{m.prospect.pharmacy_name}</div>
                  <div className="text-xs text-slate-500">{formatPeriod(m.planned_start_at, m.planned_end_at, m.timezone)} · {m.type.label}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Users size={12} />{m.lead ? m.lead.name : "Responsable à désigner"}{m.team.length > 1 ? ` +${m.team.length - 1}` : ""}</div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            ))}
          </Card>

          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="missions-count">{items.length} mission{items.length > 1 ? "s" : ""} sur {total}</span>
            {items.length < total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="missions-load-more">
                {loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

// « À valider »: the submitted reports the signed-in organiser may decide on (never one they wrote or submitted).
function ReportsToReview() {
  const [state, setState] = useState({ phase: "loading", items: [], error: "" });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const { data } = await api.get("/mission-reports/to-review");
      setState({ phase: "ready", items: data.items, error: "" });
    } catch (e) {
      setState({ phase: "error", items: [], error: missionErrorMessage(e, "Impossible de charger les comptes rendus à valider.") });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (state.phase === "loading") return <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>;
  if (state.phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3">
        <p className="text-sm text-slate-700">{state.error}</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }
  if (!state.items.length) {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="reports-review-empty">
        <ClipboardCheck className="mx-auto text-slate-300" size={26} />
        <h3 className="font-medium text-slate-900">Aucun compte rendu à valider</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">Les comptes rendus soumis par les équipes de vos missions apparaîtront ici.</p>
      </Card>
    );
  }
  return (
    <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="reports-review-list">
      {state.items.map((r) => (
        <Link key={r.mission_id} to={`/missions/${r.mission_id}/compte-rendu`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`report-review-${r.reference}`}>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-900 break-words">{r.title}</div>
            <div className="text-xs text-slate-500">{r.reference} · soumis par {r.submitted_by_name} le {formatDateTime(r.submitted_at)}{r.revision > 1 ? ` · version ${r.revision}` : ""}</div>
          </div>
          <ChevronRight size={16} className="text-slate-300 shrink-0" />
        </Link>
      ))}
    </Card>
  );
}
